import React from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Spinner } from '../../components/ui/spinner';
import { Separator } from '../../components/ui/separator';
import DateTimePicker from 'react-datetime-picker';
import Picker from 'emoji-picker-react';
import RecurrentPosts from '../../components/RecurrentPosts';
import {
  ImageIcon,
  Upload,
  Sparkles,
  Calendar,
  Share2,
  Pencil
} from 'lucide-react';

const SocialPostForm = ({
  // Estado de la imagen
  croppedImg,
  preview,
  setShowImgEditModal,

  // Estado del post
  postDescription,
  setPostDescription,
  assetUrl,
  handlePreview,
  assetAttached,

  // Estado de generación de texto
  hasSelectedImage,
  isGeneratingText,
  generateTextFromImage,

  // Referencias
  message,
  checkTw,
  resetForm,
  checkFb,
  checkGmb,
  checkIg,

  // Estado de emojis
  showEmojisMenu,
  setShowEmojisMenu,

  // Merge fields
  mergeFields,

  // Estado de fecha/hora
  isCustomTime,
  handleCustomTime,
  date,
  onChange,
  getTime,
  currentClientTimeZone,
  props,
  days,

  // Estado recurrente
  checked,
  handleRecurrentCheck,
  showRecurrent,
  setRecurrentDates,
  editTimeRecurrentDates,

  // Best time
  bestTime,

  // Redes sociales
  renderSocialNetworks,
  post,

  // Drag and drop
  onDragOver,
  onDrop,

  // Plan validation
  hasProPlan
}) => {
  return (
    <form className="space-y-6 flex-1 flex flex-col">
      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <div className="aspect-video bg-muted/30 flex items-center justify-center overflow-hidden relative">
          {croppedImg || preview ? (
            <>
              <img
                src={croppedImg || preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              {preview && (
                <button
                  type="button"
                  onClick={() => setShowImgEditModal(true)}
                  disabled={!hasProPlan}
                  className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ border: '2px solid #3766a8' }}>
                  <Pencil className="h-5 w-5 text-primary" />
                </button>
              )}
            </>
          ) : (
            <div className="text-center text-muted-foreground p-6">
              <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-base">No image preview</p>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2 mt-6">
        <Label className="text-base font-medium">Image URL</Label>
        <Input
          value={assetUrl.value}
          name="assetUrl"
          onChange={handlePreview}
          className="h-12 text-base"
          placeholder="Enter Image URL"
          disabled={!hasProPlan}
        />
      </div>

      <input
        type="file"
        ref={assetAttached}
        className="hidden"
        name="assetUrl"
        onChange={handlePreview}
        accept="image/*"
        disabled={!hasProPlan}
      />

      <Button
        type="button"
        variant="outline"
        className="w-full h-12 gap-2"
        onClick={() => assetAttached.current?.click()}
        disabled={!hasProPlan}>
        <Upload className="h-5 w-5" />
        Upload Image
      </Button>

      {hasSelectedImage && (croppedImg || preview) && (
        <Button
          type="button"
          className={`w-full h-14 gap-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all border-0 ${
            isGeneratingText ? 'btn-secondary' : 'btn-primary'
          }`}
          onClick={generateTextFromImage}
          disabled={!hasProPlan || isGeneratingText}>
          {isGeneratingText ? (
            <>
              <Spinner className="h-6 w-6" />
              &nbsp; Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-6 w-6" />
              &nbsp; Generate Caption with AI
            </>
          )}
        </Button>
      )}

      <>
        <div className="space-y-2">
          <Label className="text-base font-medium">Message</Label>
          <Textarea
            ref={message}
            className="min-h-[120px] resize-none text-base"
            onClick={() => {
              if (message.current) {
                setShowEmojisMenu(false);
              }
            }}
            onChange={(e) => {
              setPostDescription(e.target.value);
            }}
            onFocus={() => setShowEmojisMenu(false)}
            value={postDescription}
            placeholder="Write your post message here..."
            required
            onDragOver={(e) => onDragOver(e)}
            onDrop={(e) => onDrop(e)}
            disabled={!hasProPlan}
          />

          <span
            className={`text-xs ${
              checkTw.current?.checked && postDescription.length > 280
                ? 'text-red-600'
                : postDescription.length >= 2000
                ? 'text-red-600'
                : 'text-gray-500'
            }`}>
            {checkTw.current?.checked && postDescription.length > 280 && (
              <span className="mr-1">Twitter limit • </span>
            )}
            {postDescription.length > 0 ? postDescription.length : 0}
            {postDescription.length < 2000 &&
              ` / ${2000 - postDescription.length}`}
          </span>
          <button
            type="button"
            className="hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontSize: '18px',
              border: 'none',
              background: 'none',
              padding: '0',
              cursor: 'pointer'
            }}
            onClick={() => setShowEmojisMenu(!showEmojisMenu)}
            disabled={!hasProPlan}>
            😊
          </button>

          {showEmojisMenu && (
            <div className="absolute z-50 shadow-lg">
              <Picker
                onEmojiClick={(e, emoji) => {
                  const cursorPos = message.current.selectionStart;
                  const textBeforeCursor = postDescription.substring(
                    0,
                    cursorPos
                  );
                  const textAfterCursor = postDescription.substring(
                    cursorPos,
                    postDescription.length
                  );
                  setPostDescription(
                    textBeforeCursor + emoji.emoji + textAfterCursor
                  );
                }}
              />
            </div>
          )}
        </div>

        <div>
          <p className="text-sm text-muted-foreground mb-3">
            Quick insert variables:
          </p>
          <div className="flex flex-wrap gap-2">{mergeFields}</div>
        </div>
      </>

      <Separator />

      <div
        className="flex items-center justify-between"
        style={{
          pointerEvents: post.isRepairingPost || !hasProPlan ? 'none' : 'auto',
          opacity: post.isRepairingPost || !hasProPlan ? 0.5 : 1
        }}>
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-primary" />
          <Label className="text-base font-medium">Schedule Post</Label>
        </div>
        <Switch
          checked={isCustomTime}
          onCheckedChange={() => handleCustomTime()}
          disabled={!hasProPlan}
        />
      </div>
      {/* 
      {postScheduleEnabled && (
        <div className="pl-8 space-y-4">
          <Input type="text" value={scheduledDateTime} readOnly className="max-w-[220px] h-12" />
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox checked={recurringPost} onCheckedChange={(checked) => setRecurringPost(!!checked)} />
            <span className="text-base text-muted-foreground">Recurring post</span>
          </label>
        </div>
      )} */}

      {isCustomTime && (
        <div className="pl-8 space-y-4">
          <DateTimePicker
            onChange={onChange}
            disableClock
            value={date}
            minDate={getTime(currentClientTimeZone)}
            name="postSchedule"
            required
            showTime={{ use12Hours: true }}
            format="dd/MM/yyyy hh:mm a"
            clearIcon={null}
            calendarIcon={null}
            className="max-w-[220px] h-12"
          />

          {date &&
            typeof date !== 'number' &&
            props.currentClientBestTimes &&
            getTime(currentClientTimeZone).getHours() >
              parseInt(props.currentClientBestTimes[days[date.getDay()]]) &&
            date.getDate() === new Date().getDate() && (
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
                <i className="fas fa-exclamation-triangle" />
                <span>Your best time posting has passed</span>
              </div>
            )}

          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={checked}
              onCheckedChange={(checked) => handleRecurrentCheck()}
              disabled={!hasProPlan}
            />
            <span className="text-base text-muted-foreground">
              Recurring post
            </span>
          </label>

          {showRecurrent && (
            <RecurrentPosts
              date={date}
              recurrentDates={(dates) =>
                setRecurrentDates(
                  editTimeRecurrentDates(dates, currentClientTimeZone)
                )
              }
            />
          )}
        </div>
      )}

      {!isCustomTime && bestTime && (
        <div className="text-center py-4">
          <div className="text-sm text-gray-500 mb-1">Best time to post</div>
          <div className="text-2xl font-semibold text-gray-900">
            {bestTime.toLocaleString('en-US', {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            })}
          </div>
        </div>
      )}

      <Separator />
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Share2 className="h-5 w-5 text-primary" />
          <Label className="text-base font-medium">Post to</Label>
        </div>
        <div className="flex flex-wrap gap-3">{renderSocialNetworks()}</div>
      </div>

      <div>
        {props.currentClientSocialNetworks.googleMyBusiness && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Google post type options can be added here if needed */}
            </div>
          </>
        )}
      </div>

      <input
        hidden="true"
        className="btn-reset"
        type="reset"
        ref={resetForm}
        value="reset"
      />
    </form>
  );
};

export default SocialPostForm;
